(() => {
  if (customElements.get('gassas-collection')) return;

  const FACETS = ['doft', 'marke', 'for', 'konc', 'storlek', 'pris'];
  // Lowercase and strip accents so "rokelse" finds "rökelse" and "gassas" finds "gassås".
  const norm = (s) =>
    (s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  class GassasCollection extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.grid = this.querySelector('[data-grid]');
      this.items = [...this.grid.querySelectorAll('.gassas-card')];
      this.items.forEach((el, i) => {
        el.dataset.index = i;
        el._q = ' ' + norm(el.dataset.q || el.dataset.title) + ' ';
      });
      this.searchInput = this.querySelector('[data-search]');
      this.searchClear = this.querySelector('[data-search-clear]');
      this.q = [];
      this.drawer = this.querySelector('[data-drawer]');
      this.sortSelect = this.querySelector('[data-sort]');
      this.state = Object.fromEntries(FACETS.map((f) => [f, new Set()]));
      this.state.betyg = '';
      this.readUrl();
      this.bind();
      this.apply(false);
    }

    readUrl() {
      const params = new URLSearchParams(location.search);
      for (const f of FACETS) {
        const v = params.get(f);
        if (v) v.split(',').filter(Boolean).forEach((x) => this.state[f].add(x));
      }
      this.state.betyg = params.get('betyg') || '';
      const q = params.get('q') || '';
      if (this.searchInput) this.searchInput.value = q;
      this.q = norm(window.GassasAlias ? window.GassasAlias.expand(q) : q).split(' ').filter(Boolean);
      const sort = params.get('sort') || this.dataset.defaultSort || 'popular';
      if ([...this.sortSelect.options].some((o) => o.value === sort)) this.sortSelect.value = sort;
    }

    writeUrl() {
      const url = new URL(location.href);
      for (const f of FACETS) {
        const v = [...this.state[f]].join(',');
        v ? url.searchParams.set(f, v) : url.searchParams.delete(f);
      }
      this.state.betyg ? url.searchParams.set('betyg', this.state.betyg) : url.searchParams.delete('betyg');
      const q = this.searchInput ? this.searchInput.value.trim() : '';
      q ? url.searchParams.set('q', q) : url.searchParams.delete('q');
      const sort = this.sortSelect.value;
      sort && sort !== (this.dataset.defaultSort || 'popular') ? url.searchParams.set('sort', sort) : url.searchParams.delete('sort');
      history.replaceState(history.state, '', url);
    }

    bind() {
      this.querySelector('[data-open]').addEventListener('click', () => {
        if (typeof this.drawer.showModal === 'function') this.drawer.showModal();
        else this.drawer.setAttribute('open', '');
      });
      this.querySelectorAll('[data-close]').forEach((b) =>
        b.addEventListener('click', () => (this.drawer.close ? this.drawer.close() : this.drawer.removeAttribute('open')))
      );
      this.drawer.addEventListener('click', (e) => {
        if (e.target === this.drawer) this.drawer.close();
      });
      this.querySelectorAll('[data-clear]').forEach((b) => b.addEventListener('click', () => this.clear()));
      if (this.searchInput) {
        let timer;
        this.searchInput.addEventListener('input', () => {
          clearTimeout(timer);
          timer = setTimeout(() => {
            const raw = this.searchInput.value;
            this.q = norm(window.GassasAlias ? window.GassasAlias.expand(raw) : raw).split(' ').filter(Boolean);
            this.apply();
          }, 120);
        });
        this.searchInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') e.preventDefault();
          if (e.key === 'Escape') this.clearSearch();
        });
        this.searchClear?.addEventListener('click', () => {
          this.clearSearch();
          this.searchInput.focus();
        });
      }
      this.sortSelect.addEventListener('change', () => this.apply());
      this.querySelectorAll('[data-chip]').forEach((chip) =>
        chip.addEventListener('click', () => {
          const g = chip.dataset.chip;
          this.state.doft.has(g) ? this.state.doft.delete(g) : this.state.doft.add(g);
          this.apply();
        })
      );
      this.querySelectorAll('[data-facet]').forEach((input) =>
        input.addEventListener('change', () => {
          const f = input.dataset.facet;
          if (f === 'betyg') {
            this.state.betyg = input.value;
          } else {
            input.checked ? this.state[f].add(input.value) : this.state[f].delete(input.value);
          }
          this.apply();
        })
      );
    }

    clear() {
      FACETS.forEach((f) => this.state[f].clear());
      this.state.betyg = '';
      this.apply();
    }

    clearSearch() {
      if (!this.searchInput) return;
      this.searchInput.value = '';
      this.q = [];
      this.apply();
    }

    matchesSearch(el) {
      // Every word must match the start of a word in the product's text (name, brand, notes, aliases).
      for (const w of this.q) if (!el._q.includes(' ' + w)) return false;
      return true;
    }

    test(el, facet, value) {
      const d = el.dataset;
      switch (facet) {
        case 'doft':
          return (' ' + d.groups + ' ').includes(' ' + value + ' ');
        case 'marke':
          return d.vendor === value;
        case 'for':
          return d.aud === value;
        case 'konc':
          return d.conc === value;
        case 'storlek':
          return (' ' + d.sizes + ' ').includes(' ' + value + ' ');
        case 'pris': {
          const [lo, hi] = value.split('-').map(Number);
          const p = Number(d.price);
          return p >= lo && p < hi;
        }
        case 'betyg':
          return Number(d.score) >= Number(value);
      }
      return true;
    }

    matches(el, skip) {
      if (this.q.length && !this.matchesSearch(el)) return false;
      for (const f of FACETS) {
        if (f === skip || this.state[f].size === 0) continue;
        let ok = false;
        for (const v of this.state[f]) if (this.test(el, f, v)) { ok = true; break; }
        if (!ok) return false;
      }
      if (skip !== 'betyg' && this.state.betyg && !this.test(el, 'betyg', this.state.betyg)) return false;
      return true;
    }

    sortKey(el, mode) {
      const d = el.dataset;
      const score = Number(d.score) / 100;
      const votes = Number(d.votes);
      switch (mode) {
        case 'betyg':
          // Rating weighted by number of votes, so 4.5 from 60 votes does not outrank 4.4 from 6 000.
          return score ? -((votes * score + 100 * 3.9) / (votes + 100)) : 1;
        case 'roster':
          return -votes;
        case 'nyast':
          return -Number(d.created);
        case 'pris-lag':
          return Number(d.price);
        case 'pris-hog':
          return -Number(d.price);
        case 'namn':
          return d.title;
        case 'popular':
          return score ? -(score * Math.log10(votes + 10)) : 1;
        default:
          return Number(d.index);
      }
    }

    apply(write = true) {
      let shown = 0;
      for (const el of this.items) {
        const ok = this.matches(el);
        el.hidden = !ok;
        if (ok) shown++;
      }
      const mode = this.sortSelect.value;
      const sorted = [...this.items].sort((a, b) => {
        const ka = this.sortKey(a, mode);
        const kb = this.sortKey(b, mode);
        if (ka < kb) return -1;
        if (ka > kb) return 1;
        return Number(a.dataset.index) - Number(b.dataset.index);
      });
      const frag = document.createDocumentFragment();
      sorted.forEach((el) => frag.appendChild(el));
      this.grid.appendChild(frag);

      this.querySelectorAll('[data-count]').forEach((n) => (n.textContent = shown));
      this.querySelectorAll('[data-count-word]').forEach(
        (n) => (n.textContent = shown === 1 ? this.dataset.wordOne : this.dataset.wordMany)
      );
      this.querySelector('[data-empty]').hidden = shown !== 0;
      const searching = this.q.length > 0;
      const filtersOnly = this.items.some((el) => this.matchesSearch(el)) || !searching;
      const ef = this.querySelector('[data-empty-filter]');
      const es = this.querySelector('[data-empty-search]');
      if (ef && es) {
        ef.hidden = !filtersOnly;
        es.hidden = filtersOnly;
        const all = es.querySelector('[data-search-all]');
        if (all) {
          const u = new URL(all.dataset.base, location.origin);
          u.searchParams.set('q', this.searchInput.value.trim());
          u.searchParams.set('type', 'product');
          all.href = u.pathname + u.search;
        }
      }
      if (this.searchClear) this.searchClear.hidden = !this.searchInput.value;

      // Sync inputs, chips and option counts.
      this.querySelectorAll('[data-facet]').forEach((input) => {
        const f = input.dataset.facet;
        if (f === 'betyg') input.checked = input.value === this.state.betyg;
        else input.checked = this.state[f].has(input.value);
        const n = input.closest('label')?.querySelector('[data-n]');
        if (n && input.value) {
          const count = this.items.filter((el) => this.matches(el, f) && this.test(el, f, input.value)).length;
          n.textContent = count;
          input.closest('label').classList.toggle('is-empty', count === 0 && !input.checked);
        }
      });
      this.querySelectorAll('[data-chip]').forEach((chip) => {
        const on = this.state.doft.has(chip.dataset.chip);
        chip.setAttribute('aria-pressed', on ? 'true' : 'false');
        const count = this.items.filter((el) => this.matches(el, 'doft') && this.test(el, 'doft', chip.dataset.chip)).length;
        chip.hidden = count === 0 && !on;
      });

      const active = FACETS.reduce((n, f) => n + this.state[f].size, 0) + (this.state.betyg ? 1 : 0);
      const badge = this.querySelector('[data-active-count]');
      badge.hidden = active === 0;
      badge.textContent = active;
      this.querySelector('[data-active]').hidden = active === 0;
      if (write) this.writeUrl();
    }
  }

  customElements.define('gassas-collection', GassasCollection);
})();